<template>
  <div class="d-flex" v-if="badges.length>0">
    <CBadge class="mr-2"
            shape="pill"
            color="primary-light"
            v-for="badge in badges" :key="badge.key">
      <template v-if="badge.thumbUrl">
        <img :src="badge.thumbUrl" class="badge-thumb" alt=""/>
      </template>
      {{ badge.title }} {{ badge.value }}
      <span @click="removeFilter(badge.key)"><CIcon name="cipClose" class="custom-size" :height="16" /></span>
    </CBadge>
  </div>
</template>

<script>
import {mapGetters, mapActions} from "vuex"
import moment from "moment/moment";
import deliveryStatus from "@/domain/core/constant/deliveryStatus";

export default {
  name: "FilterBadges",
  emits: ['filter'],
  computed: {
    ...mapGetters('photoSearch', [
      'filters',
      'options',
      'searchQuery',
      'options',
      'tagDefinitions',
      'selectedFaceCluster'
    ]),
    ...mapGetters('event', [
      'timeZone',
    ]),
    deliveryStatus() {
      return deliveryStatus
    },
    badges() {
      // Add filter badges
      const filterBadges = Object.keys(this.filters).map(key => {
        if (!this.filters[key]) return

        let custom = {}
        if (key === 'photographer_id') custom = this.getPhotographerIdWidget(this.filters[key])
        else if (key === 'tags') custom = this.getTagsWidget(this.filters[key])
        else if (key === 'date_range') {
          if (!this.filters[key].start && !this.filters[key].end)
            return null
          custom = this.getDateRangeWidget(this.filters[key])
        }
        else if (key === 'pica_code') custom = this.getPicaCodeWidget(this.filters[key])
        else if (key === 'race_number') custom = this.getRaceNumberWidget(this.filters[key])
        else if (key === 'selected') custom = this.getSelectedWidget(this.filters[key])
        else if (key === 'delivery_status') custom = this.getDeliveryStatusWidget(this.filters[key])
        else if (key === 'cluster_id') custom = this.getClusterIdWidget(this.filters[key])
        else if (key.startsWith('tag_')) custom = this.getTagWidget(key, this.filters[key])
        else return null

        return {
          key: key,
          title: key,
          value: this.filters[key],
          ...custom
        }
      }).filter(value => value !== null)

      // Add search query badges
      const searchBadges = []
      if (this.searchQuery) {
        if (this.searchQuery.file_name) {
          searchBadges.push({
            key: 'search_file_name',
            title: 'File Name',
            value: this.searchQuery.file_name
          })
        }
        if (this.searchQuery.id) {
          searchBadges.push({
            key: 'search_id',
            title: 'ID',
            value: this.searchQuery.id
          })
        }
      }

      return [...filterBadges, ...searchBadges]
    }
  },
  methods: {
    ...mapActions('photoSearch', [
      'setSearchQuery'
    ]),
    getPhotographerIdWidget(value) {
      if (!this.options.photographers) return {}
      let photographer = this.options.photographers.find(p => p.id === value)
      return {
        title: 'Photographer',
        value: photographer ? photographer.name : ''
      }
    },
    getTagsWidget(value) {
      // filters.tags is still an array of strings for backward compatibility
      // Only media.tags in responses are JSONB objects
      if (!value || !Array.isArray(value)) return { title: 'Tags', value: '' }

      return {
        title: 'Tags',
        value: value.join(', ')
      }
    },
    getDateRangeWidget(value) {
      let text = ''
      if (value.start && value.end)
        text = this.formatFilterDate(value.start) + ' - ' + this.formatFilterDate(value.end)
      else if (value.start)
        text = 'After ' + this.formatFilterDate(value.start)
      else if (value.end)
        text = 'Before ' + this.formatFilterDate(value.end)

      return {
        title: 'Date',
        value: text
      }
    },
    getPicaCodeWidget(value) {
      return {
        title: 'Pica Code',
        value: value
      }
    },
    getRaceNumberWidget(value) {
      return {
        title: 'Bib Number',
        value: value
      }
    },
    getSelectedWidget() {
      return {
        title: 'Selected Media',
        value: ''
      }
    },
    getDeliveryStatusWidget(value) {
      return {
        title: deliveryStatus.getLabel(value),
        value: ''
      }
    },
    getClusterIdWidget(value) {
      const result = {
        title: '',
        value: ''
      }

      if (this.selectedFaceCluster) {
        result.thumbUrl = this.selectedFaceCluster.thumb_url
        // Show name if available from tags, otherwise generic label
        const tags = this.selectedFaceCluster.tags?.cluster
        if (tags?.name) {
          result.value = tags.name
        } else if (tags?.nome || tags?.cognome) {
          result.value = [tags.nome, tags.cognome].filter(Boolean).join(' ')
        } else {
          result.value = this.$pgettext('filter_badges.cluster', 'Identified person')
        }
      } else {
        result.value = this.$pgettext('filter_badges.cluster', 'Identified person')
      }

      return result
    },
    getTagWidget(key, value) {
      // Extract tag code from key: tag_squadra -> squadra
      const tagCode = key.substring(4) // Remove 'tag_' prefix

      // Find tag definition to get display name
      const tagDef = this.tagDefinitions.find(def => def.code === tagCode)
      const tagName = tagDef ? tagDef.name : tagCode

      // Format value based on type, looking up labels for select/multiselect types
      let displayValue = value
      if (tagDef && tagDef.input_config?.options) {
        const options = tagDef.input_config.options
        if (Array.isArray(value)) {
          // Multiple values (checkbox/multiselect): map each value to its label
          displayValue = value.map(v => {
            const option = options.find(opt => opt.value === v)
            return option ? option.label : v
          }).join(', ')
        } else {
          // Single value (select): find the matching option's label
          const option = options.find(opt => opt.value === value)
          displayValue = option ? option.label : value
        }
      } else if (Array.isArray(value)) {
        displayValue = value.join(', ')
      }

      return {
        title: tagName,
        value: displayValue
      }
    },
    removeFilter(key) {
      // Handle search query removal
      if (key === 'search_file_name' || key === 'search_id') {
        this.setSearchQuery(null)
        return
      }

      // Handle normal filter removal
      let newFilters = {...this.filters}
      delete newFilters[key]
      this.$emit('filter', newFilters)
    },
    formatFilterDate(date) {
      return moment(date).format('LLL')
    },
  }
}
</script>

<style scoped lang="scss">
.badge-thumb {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  object-fit: cover;
  margin-right: 4px;
  vertical-align: middle;
}
</style>